import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { ArrowLeft, CalendarPlus, MapPin, Clock } from "lucide-react";
import SEO from "../components/common/SEO";
import MarkdownBody from "../components/common/MarkdownBody";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "2-digit",
        });
    } catch {
        return iso;
    }
}

function formatDateTime(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function toICSDate(iso) {
    // Convert ISO to UTC-compact "YYYYMMDDTHHMMSSZ" used by iCalendar.
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return (
        d.getUTCFullYear() +
        pad(d.getUTCMonth() + 1) +
        pad(d.getUTCDate()) +
        "T" +
        pad(d.getUTCHours()) +
        pad(d.getUTCMinutes()) +
        pad(d.getUTCSeconds()) +
        "Z"
    );
}

function buildICS({ title, description, location, startISO, endISO }) {
    const dtStart = toICSDate(startISO);
    if (!dtStart) return null;
    const dtEnd =
        toICSDate(endISO) ||
        toICSDate(new Date(new Date(startISO).getTime() + 60 * 60 * 1000).toISOString());
    const esc = (s) => (s || "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}@skovomax.com`;
    return [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Skovomax//Insights//EN",
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTAMP:${toICSDate(new Date().toISOString())}`,
        `DTSTART:${dtStart}`,
        `DTEND:${dtEnd}`,
        `SUMMARY:${esc(title)}`,
        `DESCRIPTION:${esc(description)}`,
        location ? `LOCATION:${esc(location)}` : null,
        "END:VEVENT",
        "END:VCALENDAR",
    ]
        .filter(Boolean)
        .join("\r\n");
}

function downloadICS(post) {
    const ics = buildICS({
        title: post.title,
        description: post.excerpt || post.title,
        location: post.event_location || "",
        startISO: post.event_date,
        endISO: post.event_end,
    });
    if (!ics) return;
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(post.slug || "event")}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// --- Layout: Blog (long-form) -----------------------------------------
function BlogLayout({ post }) {
    return (
        <>
            <section className="pt-24 pb-12 border-b border-white/10">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <Link to="/insights" className="inline-flex items-center gap-2 tech-label mb-8 hover:text-white" data-testid="insight-back">
                        <ArrowLeft size={14} /> Back to Insights
                    </Link>
                    <Reveal>
                        <SectionLabel label={`${post.type} / entry`} />
                        <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight mb-6">
                            {post.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-3 mb-6">
                            {post.category && <span className="chip">{post.category}</span>}
                            {(post.tags || []).map((t) => (
                                <span key={t} className="chip">{t}</span>
                            ))}
                        </div>
                        <div className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/45">
                            PUBLISHED · {formatDate(post.published_at || post.created_at)}
                        </div>
                    </Reveal>
                </div>
            </section>
            {post.cover_image && (
                <div className="max-w-5xl mx-auto px-6 md:px-10 mt-12">
                    <img src={post.cover_image} alt={post.title} className="w-full aspect-[16/9] object-cover frame-corners" />
                </div>
            )}
            <article className="py-16 max-w-3xl mx-auto px-6 md:px-10">
                {post.excerpt && (
                    <p className="font-serif-display text-2xl md:text-3xl text-white/85 leading-snug mb-10 border-l border-[var(--accent-indigo)] pl-6">
                        {post.excerpt}
                    </p>
                )}
                <div className="prose-tech">
                    <MarkdownBody source={post.content || ""} />
                </div>
            </article>
        </>
    );
}

// --- Layout: News (compact — tighter type, sidebar byline, no pull-quote) ----
function NewsLayout({ post }) {
    return (
        <section className="pt-24 pb-20 border-b border-white/10">
            <div className="max-w-[1100px] mx-auto px-6 md:px-10">
                <Link to="/insights" className="inline-flex items-center gap-2 tech-label mb-10 hover:text-white" data-testid="insight-back">
                    <ArrowLeft size={14} /> Back to Insights
                </Link>
                <Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <aside className="md:col-span-3 space-y-6">
                            <div>
                                <div className="overline mb-2">News</div>
                                <div className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/45">
                                    {formatDate(post.published_at || post.created_at)}
                                </div>
                            </div>
                            {post.category && (
                                <div>
                                    <div className="overline mb-2">Category</div>
                                    <span className="chip chip-active">{post.category}</span>
                                </div>
                            )}
                            {(post.tags || []).length > 0 && (
                                <div>
                                    <div className="overline mb-2">Tags</div>
                                    <div className="flex flex-wrap gap-2">
                                        {post.tags.map((t) => <span key={t} className="chip">{t}</span>)}
                                    </div>
                                </div>
                            )}
                        </aside>
                        <article className="md:col-span-9">
                            <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight mb-6">
                                {post.title}
                            </h1>
                            {post.excerpt && (
                                <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-2xl">
                                    {post.excerpt}
                                </p>
                            )}
                            {post.cover_image && (
                                <div className="mb-10">
                                    <img src={post.cover_image} alt={post.title} className="w-full aspect-[16/9] object-cover frame-corners" />
                                </div>
                            )}
                            <div className="prose-tech">
                                <MarkdownBody source={post.content || ""} />
                            </div>
                        </article>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

// --- Layout: Event (date-first hero + ICS download + location CTA) ----
function EventLayout({ post }) {
    const eventDate = post.event_date ? new Date(post.event_date) : null;
    const day = eventDate ? String(eventDate.getDate()).padStart(2, "0") : "—";
    const month = eventDate
        ? eventDate.toLocaleDateString(undefined, { month: "short" }).toUpperCase()
        : "TBD";
    const year = eventDate ? eventDate.getFullYear() : "";
    const time = eventDate
        ? eventDate.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
        : "";
    const isPast = eventDate && eventDate.getTime() < Date.now();

    return (
        <>
            <section className="pt-24 pb-16 border-b border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 photonic-grid opacity-20 pointer-events-none" />
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 relative">
                    <Link to="/insights" className="inline-flex items-center gap-2 tech-label mb-10 hover:text-white" data-testid="insight-back">
                        <ArrowLeft size={14} /> Back to Insights
                    </Link>
                    <Reveal>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
                            {/* Date glyph */}
                            <div className="md:col-span-3" data-testid="event-date-glyph">
                                <div className="tech-card !p-6 text-center border-[var(--accent-indigo)]/40">
                                    <div className="font-mono-tech text-[11px] tracking-[0.25em] uppercase text-[var(--accent-indigo)] mb-2">
                                        {month}
                                    </div>
                                    <div className="font-serif-display text-7xl leading-none text-white">
                                        {day}
                                    </div>
                                    <div className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/55 mt-3">
                                        {year}
                                    </div>
                                    {isPast && (
                                        <div className="mt-4 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">
                                            · past event ·
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Title + meta */}
                            <div className="md:col-span-9">
                                <SectionLabel label="Event / entry" />
                                <h1 className="font-serif-display text-4xl md:text-6xl leading-[1] tracking-tight mb-6">
                                    {post.title}
                                </h1>
                                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-white/70 text-sm mb-6">
                                    {eventDate && (
                                        <span className="inline-flex items-center gap-2">
                                            <Clock size={14} className="text-[var(--accent-indigo)]" />
                                            {formatDateTime(post.event_date)} {time ? "local" : ""}
                                        </span>
                                    )}
                                    {post.event_location && (
                                        <span className="inline-flex items-center gap-2" data-testid="event-location">
                                            <MapPin size={14} className="text-[var(--accent-indigo)]" />
                                            {post.event_location}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    {post.category && <span className="chip chip-active">{post.category}</span>}
                                    {(post.tags || []).map((t) => (
                                        <span key={t} className="chip">{t}</span>
                                    ))}
                                </div>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    {eventDate && !isPast && (
                                        <button
                                            type="button"
                                            onClick={() => downloadICS(post)}
                                            className="btn-primary !py-2"
                                            data-testid="event-add-to-calendar"
                                        >
                                            <CalendarPlus size={14} /> Add to calendar
                                        </button>
                                    )}
                                    {post.event_register_url && (
                                        <a
                                            href={post.event_register_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn-secondary !py-2"
                                            data-testid="event-register"
                                        >
                                            Register / RSVP
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
            {post.cover_image && (
                <div className="max-w-5xl mx-auto px-6 md:px-10 mt-12">
                    <img src={post.cover_image} alt={post.title} className="w-full aspect-[21/9] object-cover frame-corners" />
                </div>
            )}
            <article className="py-16 max-w-3xl mx-auto px-6 md:px-10">
                {post.excerpt && (
                    <p className="font-serif-display text-2xl md:text-3xl text-white/85 leading-snug mb-10">
                        {post.excerpt}
                    </p>
                )}
                <div className="prose-tech">
                    <MarkdownBody source={post.content || ""} />
                </div>
            </article>
        </>
    );
}

export default function InsightDetail() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/posts/${slug}`);
                if (!cancel) setPost(data);
            } catch (e) {
                if (!cancel) setErr("Post not found");
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [slug]);

    const seo = useMemo(() => {
        if (!post) return null;
        return {
            title: `${post.title} · Skovomax Insights`,
            description: post.excerpt || `${post.type} — Skovomax Insights`,
            image: post.cover_image,
            type: "article",
            jsonLd: {
                "@context": "https://schema.org",
                "@type": post.type === "event" ? "Event" : "Article",
                headline: post.title,
                description: post.excerpt,
                image: post.cover_image ? [post.cover_image] : undefined,
                datePublished: post.published_at || post.created_at,
                dateModified: post.updated_at || post.published_at || post.created_at,
                author: { "@type": "Organization", name: "Skovomax" },
                publisher: { "@type": "Organization", name: "Skovomax" },
                ...(post.type === "event" && post.event_date ? { startDate: post.event_date } : {}),
                ...(post.type === "event" && post.event_location
                    ? { location: { "@type": "Place", name: post.event_location } }
                    : {}),
            },
        };
    }, [post]);

    if (loading) {
        return <div className="max-w-3xl mx-auto px-6 md:px-10 py-28 tech-label">Loading…</div>;
    }
    if (err || !post) {
        return (
            <div className="max-w-3xl mx-auto px-6 md:px-10 py-28">
                <div className="tech-label mb-4">{err}</div>
                <Link to="/insights" className="btn-secondary">Back to Insights</Link>
            </div>
        );
    }

    return (
        <>
            {seo && <SEO {...seo} />}
            {post.type === "event" ? (
                <EventLayout post={post} />
            ) : post.type === "news" ? (
                <NewsLayout post={post} />
            ) : (
                <BlogLayout post={post} />
            )}
        </>
    );
}
